// The chosen names of a Multi-select custom field on one line: as many whole names as fit, separated by ", ",
// then "+N" for the rest. A name is never cut in the middle, except the first when not even it fits beside the
// "+N", which then ends in an ellipsis. The fit is measured: each name's width in the box's own font, on a
// canvas so nothing extra is drawn, against the box's rendered width, read again whenever the box resizes (a
// column dragged, a panel opened) and once web fonts have loaded. Until the box has a width, every name is
// shown and the box's edge cuts the line.
// When anything is hidden, hovering over the names or focusing them shows every name in the design-system
// hover box; when nothing is, the hover box stays shut and the line is no tab stop (unless it holds the focus,
// which a resize must not take away). The accessible name always lists every chosen name after the field's
// name. Names follow the option list's order; a stored id with no option is skipped.
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {unstable_batchedUpdates} from 'react-dom';
import Styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Tooltip, tokensForThemeProvider} from '@forecast-it/design-system';
import {CustomFieldTooltipText} from '../forecast-app/shared/components/custom-field-columns/CustomFieldTooltipText';
import {useElementWidth} from '../forecast-app/shared/components/custom-field-columns/useIsTruncatedWidth';
import {multiSelectNames} from '../forecast-app/shared/util/customFieldValueValidation';
import {fitNames, MULTI_SELECT_SEPARATOR, type NamesFit} from './multiSelectFit';

// The gap between the last name shown and "+N".
const MORE_GAP = tokensForThemeProvider.sky.sys.spacing['100'];
const MORE_GAP_PX = parseFloat(MORE_GAP) || 0;
// The browser rounds a box's width; half a pixel keeps a line that fits exactly from reading as one that does not.
const FIT_TOLERANCE_PX = 0.5;

// One line that never wraps. min-width 0 lets it shrink inside a flex cell; max-width keeps it inside its
// container whatever width it is given.
const Line = Styled.span<{$align: 'start' | 'center'}>`
	display: flex;
	align-items: center;
	justify-content: ${({$align}) => ($align === 'center' ? 'center' : 'flex-start')};
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
	white-space: nowrap;
`;
// The names may shrink, which cuts the first one with an ellipsis; the "+N" never does.
const Names = Styled.span`
	flex: 0 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
`;
const More = Styled.span`
	flex: none;
	margin-left: ${MORE_GAP};
`;

// Read by assistive technology, never drawn.
const VisuallyHidden = Styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

// Makes each field's label id unique on the page (React 16 has no useId).
let lastLabelId = 0;

// One canvas context for every field on the page, created on first use: a page of cells measures often, and a
// canvas per measure is garbage the browser has to collect.
let sharedContext: CanvasRenderingContext2D | null | undefined;
const canvasContext = (): CanvasRenderingContext2D | null => {
	if (sharedContext === undefined) sharedContext = document.createElement('canvas').getContext('2d');
	return sharedContext;
};

// Measures text in a box's font on a canvas. Canvas text leaves out the box's letter spacing, which is added
// for each character. Without a canvas every text measures 0, so every name is shown and the box's edge cuts
// the line.
// Reads the box's font once, which makes the browser recalculate styles, and returns a measure in that font. The
// canvas is shared, so each measure sets the font again: another field may have measured in its own since.
const textMeasurer = (box: HTMLElement): ((text: string) => number) => {
	const context = canvasContext();
	const style = window.getComputedStyle(box);
	const font =
		style.fontSize && style.fontFamily
			? `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
			: null;
	const letterSpacing = parseFloat(style.letterSpacing) || 0;
	return text => {
		if (!context) return 0;
		if (font) context.font = font;
		return context.measureText(text).width + letterSpacing * text.length;
	};
};

// The page's web fonts, watched once for every field: a measure taken before they load is in a fallback font.
// The version goes up when the fonts the page is waiting on have loaded, and every field mounted by then measures
// again, all in one render. A field mounted later measures in the loaded fonts, so it waits for nothing. The
// browser hands out a new ready promise when more fonts start loading, which is watched in turn.
type FontFaceSetLike = {ready: Promise<unknown>; status?: string};
let fontsVersion = 0;
let watchedFonts: Promise<unknown> | null = null;
const fontsListeners = new Set<() => void>();
const watchFonts = (): void => {
	const fonts = (document as unknown as {fonts?: FontFaceSetLike}).fonts;
	if (!fonts || fonts.ready === watchedFonts) return;
	const {ready} = fonts;
	watchedFonts = ready;
	if (fonts.status === 'loaded') return;
	ready.then(() => {
		fontsVersion += 1;
		unstable_batchedUpdates(() => fontsListeners.forEach(listener => listener()));
	});
};
const useFontsVersion = (): number => {
	const [version, setVersion] = useState(fontsVersion);
	useEffect(() => {
		const listener = () => setVersion(fontsVersion);
		fontsListeners.add(listener);
		watchFonts();
		// The fonts may have loaded between this field's render and now.
		if (fontsVersion !== version) listener();
		return () => {
			fontsListeners.delete(listener);
		};
	}, []);
	return version;
};

// The widths fitNames needs, measured once for each set of names, box and font load.
type Measured = {
	nameWidths: number[];
	separatorWidth: number;
	// The width of "+N" and the gap before it, measured the first time each count is asked for.
	moreWidth: (hidden: number) => number;
};

type Option = {id: string; displayName: string};

export type CustomFieldMultiSelectTextProps = {
	// The stored value: a JSON array of option ids.
	value: string | null | undefined;
	// The field's options with their GraphQL ids, in the option list's order.
	options: ReadonlyArray<Option>;
	// The field's name, which starts the accessible name.
	label: string;
	// Shown in place of the names when no option is chosen, such as '-' in a table cell.
	empty?: React.ReactNode;
	// Off inside CustomFieldMultiSelectInput, which puts the hover box on its own trigger.
	tooltip?: boolean;
	// Off where the names sit inside a link or a button: the hover box then opens on hover alone, and the
	// link or button's own name lists every value.
	focusable?: boolean;
	// Centred in a centred column; at the start everywhere else.
	align?: 'start' | 'center';
	// Called with whether anything is hidden ("+N" shown, or the first name cut).
	onHiddenChange?: (hidden: boolean) => void;
	'data-cy'?: string;
};

export const CustomFieldMultiSelectText = ({
	value,
	options,
	label,
	empty = null,
	tooltip = true,
	focusable = true,
	align = 'start',
	onHiddenChange,
	'data-cy': dataCy = 'cf-multi-select',
}: CustomFieldMultiSelectTextProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const names = multiSelectNames(value, options);
	const namesKey = names.join('\n');
	const [box, setBox] = useState<HTMLSpanElement | null>(null);
	const [observeWidth, available, measureWidth] = useElementWidth<HTMLSpanElement>();
	// The hover box's trigger hands the line a new ref on every render, so React detaches it and attaches it
	// again each time, which would render the field twice. Only an attach is taken: the line is removed only
	// with the whole field or when no name is chosen, and a new line attaches when names are chosen again.
	const attach = useCallback(
		(node: HTMLSpanElement | null) => {
			if (node === null) return;
			setBox(node);
			observeWidth(node);
		},
		[observeWidth]
	);
	const [labelId] = useState(() => `cf-multi-select-label-${(lastLabelId += 1)}`);
	const fontsLoaded = useFontsVersion();
	const [measured, setMeasured] = useState<Measured | null>(null);
	const [focused, setFocused] = useState(false);

	// Measured before paint, and only when the names change, the box first appears or the fonts load: a resize
	// refits these widths without reading the font again.
	useLayoutEffect(() => {
		if (box === null || names.length === 0) {
			setMeasured(null);
			// A line removed while focused reports no blur.
			setFocused(false);
			return;
		}
		const measure = textMeasurer(box);
		const moreWidths = new Map<number, number>();
		setMeasured({
			nameWidths: names.map(measure),
			separatorWidth: measure(MULTI_SELECT_SEPARATOR),
			moreWidth: hidden => {
				let width = moreWidths.get(hidden);
				if (width === undefined) {
					width = measure(formatMessage({id: 'custom_field.multi_select.more'}, {count: hidden})) + MORE_GAP_PX;
					moreWidths.set(hidden, width);
				}
				return width;
			},
		});
		// The names are read through namesKey, which changes exactly when they do.
	}, [box, namesKey, fontsLoaded]);

	const lineWidth = useMemo(
		() =>
			measured === null
				? null
				: measured.nameWidths.reduce((sum, width) => sum + width, 0) +
				  measured.separatorWidth * (measured.nameWidths.length - 1),
		[measured]
	);
	// The line sizes itself to its names (below), so in a wide box the width last read is the line's own old one.
	// When the names change it is read again before paint, so names that now fit are never hidden for a frame
	// while the observer catches up. Not on mount, which waits for the observer's first width like any resize.
	useLayoutEffect(() => {
		if (available !== null) measureWidth();
	}, [lineWidth]);
	// Arithmetic over the measured widths, so it runs on every width change.
	const fit: NamesFit | null = useMemo(
		() =>
			measured === null || available === null
				? null
				: fitNames(measured.nameWidths, available + FIT_TOLERANCE_PX, measured.separatorWidth, measured.moreWidth),
		[measured, available]
	);

	const shown = fit === null ? names.length : fit.shown;
	const hiddenCount = names.length - shown;
	const hidden = fit !== null && (hiddenCount > 0 || fit.cutFirst);
	useEffect(() => {
		onHiddenChange?.(hidden);
	}, [hidden]);

	if (names.length === 0) return <>{empty}</>;

	const allNames = names.join(MULTI_SELECT_SEPARATOR);
	// The hover box opens on its trigger's focus and pointer move unless an earlier handler on the trigger
	// cancelled the event. Cancelling while every name is visible keeps it shut without adding or removing the
	// Tooltip around the line, which would remount the line and lose its focus.
	const keepTooltipShut = (event: React.SyntheticEvent): void => {
		if (!hidden) event.preventDefault();
	};
	// As wide as the whole line and no wider than its container (max-width), so a container sized by its
	// content keeps the width the names need rather than shrinking to the names shown.
	// The full list is real text, visually hidden, rather than an aria-label: a link or a button around the names
	// takes its name from its content, which reads text reliably and a nested label not always. The group, a tab
	// stop when names are hidden, is named by the same text.
	const line = (
		<Line
			ref={attach}
			role="group"
			aria-labelledby={labelId}
			tabIndex={(hidden || focused) && tooltip && focusable ? 0 : undefined}
			onFocus={
				tooltip
					? (event: React.FocusEvent) => {
							setFocused(true);
							keepTooltipShut(event);
					  }
					: undefined
			}
			onBlur={tooltip ? () => setFocused(false) : undefined}
			onPointerMove={tooltip ? keepTooltipShut : undefined}
			style={lineWidth === null ? undefined : {width: Math.ceil(lineWidth) + 1}}
			$align={align}
			data-cy={dataCy}
		>
			<VisuallyHidden id={labelId}>
				{formatMessage({id: 'custom_field.multi_select.aria_label'}, {field: label, values: allNames})}
			</VisuallyHidden>
			<Names aria-hidden="true" data-cy={`${dataCy}-names`}>
				{names.slice(0, shown).join(MULTI_SELECT_SEPARATOR)}
			</Names>
			{hiddenCount > 0 ? (
				<More aria-hidden="true" data-cy={`${dataCy}-more`}>
					{formatMessage({id: 'custom_field.multi_select.more'}, {count: hiddenCount})}
				</More>
			) : null}
		</Line>
	);
	// Interactable only so the design-system Tooltip does not cancel a click on the names, which may open an
	// editor or follow a row link; its content is plain text.
	return tooltip ? (
		<Tooltip content={<CustomFieldTooltipText>{allNames}</CustomFieldTooltipText>} interactable>
			{line}
		</Tooltip>
	) : (
		line
	);
};

export default CustomFieldMultiSelectText;
